import React from "react";
import { useLocation } from "react-router-dom";

type Props = {
  children: React.ReactNode;
  /** When this changes after a crash the boundary tries to render its children again (e.g. on navigation). */
  resetKey?: string;
};

type State = { hasError: boolean };

/**
 * Keeps a crash in one screen from blanking the whole app, which matters because the app is
 * embedded in other apps. The fallback deliberately uses no translations, stencil components or
 * recoil state: any of those may be what failed.
 */
export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error("Screen crashed:", error, info.componentStack);
  }

  componentDidUpdate(prevProps: Props) {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false });
    }
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div role="alert" data-testid="error-boundary-fallback" style={{ padding: 24, textAlign: "center" }}>
        <p>Something went wrong.</p>
        <button onClick={() => window.location.reload()}>Reload</button>
      </div>
    );
  }
}

/** ErrorBoundary that recovers when the route changes. Must be rendered inside a Router. */
export const RouteErrorBoundary: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { pathname } = useLocation();
  return <ErrorBoundary resetKey={pathname}>{children}</ErrorBoundary>;
};
